/**
 * Navigation Handlers
 *
 * Pure functions that apply commands to state.
 */

import { NavigationState } from './state';
import { NavigationCommand } from './commands';
import { CommandMeta } from '@commandDrivenStateEngine/core/types';

type ExtractCommand<T extends string> = Extract<NavigationCommand, { type: T }>;

/**
 * Handle navigation request
 */
const handleNavigateRequest = (
  state: NavigationState,
  command: ExtractCommand<'navigation/request/navigate'>,
  meta: CommandMeta
): NavigationState => {
  return {
    ...state,
    pendingNavigation: {
      path: command.payload.path,
      params: command.payload.params || {},
      source: meta.source as 'user' | 'ai' | 'url'
    }
  };
};

/**
 * Clear pending navigation
 */
const handleClearRequest = (
  state: NavigationState,
  _command: ExtractCommand<'navigation/request/clear'>
): NavigationState => {
  return {
    ...state,
    pendingNavigation: null
  };
};

/**
 * Update current location (from React Router)
 */
const handleLocationUpdate = (
  state: NavigationState,
  command: ExtractCommand<'navigation/location/update'>
): NavigationState => {
  return {
    ...state,
    currentPath: command.payload.path,
    currentParams: command.payload.params
  };
};

/**
 * Record navigation in history
 */
const handleRecordHistory = (
  state: NavigationState,
  command: ExtractCommand<'navigation/history/record'>,
  meta: CommandMeta
): NavigationState => {
  const newEntry = {
    path: command.payload.path,
    params: command.payload.params,
    timestamp: meta.timestamp,
    source: meta.source as 'user' | 'ai' | 'url'
  };

  return {
    ...state,
    history: [ ...state.history, newEntry ]
  };
};

/**
 * Handler map
 */
export const handlers = {
  'navigation/request/navigate': handleNavigateRequest,
  'navigation/request/clear': handleClearRequest,
  'navigation/location/update': handleLocationUpdate,
  'navigation/history/record': handleRecordHistory
};
